/**
 * ============================================================================
 * PERMISSIONS API — RTK Query endpoint for getPermissions.
 * ============================================================================
 *
 * The access profile is constant for a session: fetched ONCE
 * (keepUnusedDataFor: Infinity, no refetch on focus), in parallel with
 * everything else. Consumers: the header's dashboard switcher (spec
 * multi-dashboard R1/R2); feature/operation gating is tracked follow-up.
 *
 * FAIL-OPEN TO THE DEFAULT (KFE): if the call errors (or the API isn't
 * deployed on the stage yet), selectServedDashboards falls back to the
 * default-dashboard-only list — the shell never breaks on a missing
 * permissions call (R1.3). KFE is an open dashboard, so the fallback never
 * over-grants.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { GetPermissionsApi } from 'src/accessors/GetPermissionsApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import { currentUserAlias } from 'src/accessors/currentUser';
import {
  ALL_GRANT_ID,
  DashboardAccess,
  GetPermissionsOutput,
  NO_LOCK_SENTINEL,
} from 'src/types/permissionsApi.types';
import { WBR_DASHBOARD_ID, WBR_REVIEW_TYPE } from 'src/constants';
import { dashboardStructure } from 'src/config/dashboards';

export const permissionsApi = createApi({
  reducerPath: 'permissionsApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  keepUnusedDataFor: Infinity,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    // Cache key is the void arg: one profile per session. The actor is read
    // at call time from the Mons meta tag (the server re-resolves it anyway).
    getPermissions: builder.query<GetPermissionsOutput, void>({
      queryFn: async () => {
        try {
          return {
            data: await GetPermissionsApi.getPermissions({
              actorId: currentUserAlias(),
              actorType: 'principal',
            }),
          };
        } catch (error) {
          if (error instanceof ReportingApiError) {
            return {
              error: {
                status: error.status,
                data: error.body ?? error.errorCode,
              },
            };
          }
          return { error: { status: 0, data: String(error) } };
        }
      },
    }),
  }),
});

export const useGetPermissionsQuery = permissionsApi.useGetPermissionsQuery;

/** The default-dashboard-only fallback: KFE, the open default (R1.3 / R2.4). */
export const FALLBACK_DASHBOARDS: DashboardAccess[] = [
  {
    dashboardId: WBR_DASHBOARD_ID,
    name: dashboardStructure(WBR_DASHBOARD_ID).displayName,
    supportedReviewTypes: [WBR_REVIEW_TYPE],
    accessible: true,
    requiredBindleLockId: NO_LOCK_SENTINEL,
  },
];

/**
 * Is a feature accessible in the caller's profile? Features are surfaced by
 * GetPermissions purely as a UI VISIBILITY gate (`Feature.CHAT_AGENT` etc.),
 * and the synthetic "all" grant entry is a lock marker, not a real feature, so
 * it never matches.
 *
 * FAIL-CLOSED (unlike selectServedDashboards, which fails open to KFE): an
 * absent/failed profile, or an absent feature entry, reads as NOT accessible.
 * A gated entry point stays hidden until the profile confirms access — showing
 * it to an unconfirmed caller would only surface a 403 from the gated API.
 */
export function selectFeatureAccessible(
  output: GetPermissionsOutput | undefined,
  featureId: string,
): boolean {
  const feature = output?.features?.find(
    (f) => f.featureId === featureId && f.featureId !== ALL_GRANT_ID,
  );
  return feature?.accessible ?? false;
}

/**
 * The served dashboards for pickers: the API's list minus the synthetic
 * "all" grant entry (a lock marker, not a dashboard), in server order. An
 * absent/failed profile yields the default-dashboard-only fallback.
 */
export function selectServedDashboards(
  output: GetPermissionsOutput | undefined,
): DashboardAccess[] {
  if (!output?.dashboards?.length) return FALLBACK_DASHBOARDS;
  const served = output.dashboards
    .filter((d) => d.dashboardId !== ALL_GRANT_ID)
    // The DEFAULT dashboard (KFE, wire id "fai") lists first in every picker (R2.4);
    // the rest keep server order. Array.sort is stable.
    .sort(
      (a, b) =>
        Number(b.dashboardId === WBR_DASHBOARD_ID) -
        Number(a.dashboardId === WBR_DASHBOARD_ID),
    );
  return served.length > 0 ? served : FALLBACK_DASHBOARDS;
}
